import Text from "@ingka/text";
import Checkbox from "@ingka/checkbox";
import classNames from "classnames";
import React, { useState } from "react";
import { useRemoveAssetMutation } from "@/hooks/assets";
import AssetListModal from "./AssetListModal";
import AssetItemView from "./AssetItemView";
import { useStore } from "@/services/store";
import { useShallow } from "zustand/react/shallow";
import { isAssetValidationEntry } from "@/services/store/slices/mandatoryMetaData/types";
import InlineMessage from "@ingka/inline-message/InlineMessage";
import { isUploadFinalized } from "@/components/MetadataFields/formFieldsAccessRules";

const AssetListView = () => {
  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useState(false);

  const {
    onAssetCheckboxChange,
    onAssetClick,
    onSelectAllCheckboxChange,
    state,
  } = useStore(
    useShallow((state) => ({
      state,
      onAssetClick: state.setActiveAsset,
      onAssetCheckboxChange: state.toggleAssetCheckbox,
      onSelectAllCheckboxChange: state.toggleSetAllCheckbox,
    })),
  );
  const removeAsset = useRemoveAssetMutation();

  const onRemoveAsset = (assetId: number) => async () => {
    const isLastAsset = state.assets.length === 1;
    if (isLastAsset) {
      setIsDeleteDraftModalOpen(true);
      return;
    }
    await removeAsset.mutateAsync(assetId);
  };

  const assets = state.assets.map(
    ({ id, name, filename, isActive, isChecked }) => ({
      id: Number(id),
      name: name || String(filename),
      isActive,
      isChecked,
    }),
  );

  const uploadError = state.validationErrors.find(
    (e) => !isAssetValidationEntry(e),
  );

  const allChecked = assets.every((asset) => asset.isChecked);
  return (
    <>
      <Text tagName="h3" headingSize="xs" className="mb-space100 pl-space150">
        Select files to edit
      </Text>
      <div
        className={classNames("asset-list-view", {
          "active-border": allChecked,
        })}
        onClick={() => onSelectAllCheckboxChange(true)}
      >
        <Text
          tagName="span"
          bodySize="m"
          className="pl-space150"
          id="select-all-label"
        >
          Edit all
        </Text>
        <Checkbox
          id="select-all-id"
          value=""
          aria-labelledby="select-all-label"
          checked={allChecked}
          indeterminate={!allChecked}
          disabled={isUploadFinalized({ uploadState: state.uploadState })}
          onChange={(e) => onSelectAllCheckboxChange(e.target.checked)}
          className="pr-space50"
        />
      </div>
      <div className="asset__list mt-space150 max-h-[36rem] overflow-y-auto">
        {uploadError && (
          <InlineMessage
            variant="negative"
            title="Upload Error"
            body={uploadError.message}
          />
        )}
        {assets.map(({ id, isActive, isChecked }) => (
          <React.Fragment key={id}>
            <AssetItemView
              assetId={id}
              isActive={isActive}
              isChecked={isChecked}
              allChecked={allChecked}
              onAssetClick={onAssetClick}
              onAssetChecked={onAssetCheckboxChange}
              onRemoveAsset={onRemoveAsset(id)}
            />
            <hr />
          </React.Fragment>
        ))}
      </div>
      <AssetListModal
        isModalOpen={isDeleteDraftModalOpen}
        setIsModalOpen={setIsDeleteDraftModalOpen}
        removeAssetsUpload={() => removeAsset.mutateAsync(assets[0].id)}
      />
    </>
  );
};

export default AssetListView;
